import { forwardRef, MouseEventHandler, useEffect, useState } from "react";
import {
  FormikComputedProps,
  FormikHandlers,
  FormikHelpers,
  FormikState,
  useFormik,
} from "formik";
import cx from "classnames";
import Grid from "@/components/Grid";
import Typography from "@/components/Typography";
import TextField from "@/components/TextField";
import { FormActions } from "@/components/FormController";
import DatePicker from "@/components/DatePicker";
import styles from "./PersonalDetailsForm.module.scss";
import { usePersonalDetailsQuery } from "@/fetch/profiles";
import { useGeneralSettingsQuery } from "@/fetch/trip";
import {
  QuestionTextLoader,
  RadioLoader,
  TextFieldLoader as FormLoader,
} from "./PersonalDetailsFormLoader";
import { LockIcon } from "@/components/Icon";
import Radio, { RadioGroup } from "@/components/Radio";
import { FormControlLabel } from "@/components/FormController";
import {
  TravellerPreferencesFormType,
  Status,
} from "@/components/PersonalDetailsForms";
import {
  usePersonalDetailsFormInitialValues,
  usePersonalDetailsFormSubmission,
  usePersonalDetailsFormValidation,
} from "@/components/PersonalDetailsForms/hooks";
import NationalitySelector from "@/components/NationalitySelector";
import dayjs from "dayjs";
import Checkbox from "@/components/Checkbox";
import { Skeleton } from "@/components/Loader";
import { Nationality } from "@/hooks/useNationalities";
import theme from "@/theme/theme";

interface FormTemplateProp<FormType> {
  isFormSubmitting: boolean;
  formik: FormikState<FormType> &
    FormikComputedProps<FormType> &
    FormikHelpers<FormType> &
    FormikHandlers;
  isFormDataLoading: boolean;
  isProfileSetup?: boolean;
}

function TravellerPreferencesFormTemplateWithRef<
  T extends TravellerPreferencesFormType
>(
  {
    isFormSubmitting,
    formik,
    isFormDataLoading,
    isProfileSetup,
  }: FormTemplateProp<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  return (
    <div ref={ref}>
      <Grid
        container
        spacing="8px"
        columnSpacing="12px"
        className={styles.gridContainer}
      >
        <Grid item xs={12} md={12} lg={12}>
          {isFormDataLoading ? (
            QuestionTextLoader
          ) : (
            <Typography
              variant="subtitle1"
              className={styles.question}
              color="text.secondary"
            >
              All Getaways tours include social twin-share accommodation with a
              fellow group member of the same gender as standard. If you prefer
              your own space, you can easily request a Single Room Upgrade by
              chatting directly with your dedicated Trip Coordinator after
              booking.
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={12} lg={12}>
          {isFormDataLoading ? (
            <Skeleton variant="text" />
          ) : (
            <div className={styles.checkboxContainer}>
              {formik.initialValues
                ?.getawayDiffGenderRoomSharingIsComfortable !== null &&
                !!isProfileSetup && (
                  <LockIcon
                    fontSize="small"
                    variant="outlined"
                    htmlColor={theme.palette.action.disabled}
                    style={{ marginTop: 4 }}
                  />
                )}
              <Checkbox
                color="primary"
                checked={
                  formik.values.getawayDiffGenderRoomSharingIsComfortable
                }
                onChange={(event) =>
                  formik.setFieldValue(
                    "getawayDiffGenderRoomSharingIsComfortable",
                    event.target.checked
                  )
                }
                className={cx(styles.checkbox, {
                  [styles.disabledCheckbox]:
                    isFormSubmitting ||
                    formik.initialValues
                      ?.getawayDiffGenderRoomSharingIsComfortable !== null,
                })}
                disabled={
                  isFormSubmitting ||
                  (formik.initialValues
                    ?.getawayDiffGenderRoomSharingIsComfortable !== null &&
                    !!isProfileSetup)
                }
              />
              <Typography variant="subtitle2">
                Please tick this box if you are 100% comfortable sharing a
                twin-share room with a traveller of a{" "}
                <span className={styles.underline}>different gender</span> to
                help us optimise group rooming.
              </Typography>
            </div>
          )}
        </Grid>
        <Grid item xs={12} md={12} lg={12}>
          {isFormDataLoading ? (
            <Skeleton variant="text" />
          ) : (
            <div className={styles.checkboxContainer}>
              {formik.initialValues?.getawayPrivateRoomUpgradeIsRequested !==
                null &&
                !!isProfileSetup && (
                  <LockIcon
                    fontSize="small"
                    variant="outlined"
                    htmlColor={theme.palette.action.disabled}
                    style={{ marginTop: 4 }}
                  />
                )}
              <Checkbox
                color="primary"
                checked={formik.values.getawayPrivateRoomUpgradeIsRequested}
                onChange={(event) =>
                  formik.setFieldValue(
                    "getawayPrivateRoomUpgradeIsRequested",
                    event.target.checked
                  )
                }
                className={cx(styles.checkbox, {
                  [styles.disabledCheckbox]:
                    isFormSubmitting ||
                    formik.initialValues
                      ?.getawayPrivateRoomUpgradeIsRequested !== null,
                })}
                disabled={
                  isFormSubmitting ||
                  (formik.initialValues
                    ?.getawayPrivateRoomUpgradeIsRequested !== null &&
                    !!isProfileSetup)
                }
              />
              <Typography variant="subtitle2">
                If you would like to upgrade your trip to a private room, please
                check this box and your Trip Coordinator will provide you with
                more details.
              </Typography>
            </div>
          )}
        </Grid>
      </Grid>
    </div>
  );
}

const TravellerPreferencesForm: React.FC<{
  onSubmitted?: () => void;
  primaryButtonLabel?: string;
  secondaryButtonLabel?: string;
  secondaryButtonAction?: MouseEventHandler<HTMLButtonElement> | undefined;
  correlationId?: string;
  onSubmitting?: (isFormSubmitting: boolean) => void;
}> = ({
  onSubmitted,
  primaryButtonLabel = "Save",
  secondaryButtonLabel,
  secondaryButtonAction,
  correlationId = "",
  onSubmitting,
}) => {
  const [isRefetchingForm, setRefetchingForm] = useState(false);
  const { data: generalSettingsData, refetch: generalSettingsRefetch } =
    useGeneralSettingsQuery();

  const { travellerPreferencesValidationSchema } =
    usePersonalDetailsFormValidation();

  const { submitTravellerPreferencesForm, isCreatingGeneralSettings } =
    usePersonalDetailsFormSubmission();

  const {
    travellerPreferencesInitialValues,
    isLoading: isInitialDataLoading,
    isProfileSetup,
  } = usePersonalDetailsFormInitialValues();

  const isFormDataLoading = isInitialDataLoading;
  const isFormSubmitting = isCreatingGeneralSettings || isRefetchingForm;

  useEffect(() => {
    onSubmitting?.(isFormSubmitting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormSubmitting]);

  const onSubmit = async (values: TravellerPreferencesFormType) => {
    try {
      await submitTravellerPreferencesForm(values);

      setRefetchingForm(true);
      await Promise.all([generalSettingsRefetch()]);
      onSubmitted?.();
    } catch (error) {
      console.error(error);
    } finally {
      setRefetchingForm(false);
    }
  };

  const initialValues = travellerPreferencesInitialValues;

  const formik = useFormik({
    initialValues,
    validationSchema: travellerPreferencesValidationSchema,
    onSubmit,
  });

  useEffect(() => {
    if (!generalSettingsData) return;
    formik.resetForm({ values: initialValues });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalSettingsData]);

  return (
    <>
      <TravellerPreferencesFormTemplate
        formik={formik}
        isFormDataLoading={isFormDataLoading}
        isFormSubmitting={isFormSubmitting}
        isProfileSetup={isProfileSetup}
      />

      <FormActions
        primaryButton={{
          label: primaryButtonLabel,
          action: () => formik.handleSubmit(),
          isLoading: isFormSubmitting,
          isDisabled: Boolean(onSubmitted) ? isFormSubmitting : !formik.dirty,
          className: styles.submitButton,
        }}
        secondaryButton={{
          label: secondaryButtonLabel,
          action: secondaryButtonAction,
          className: styles.secondaryButton,
        }}
      />
    </>
  );
};
const TravellerPreferencesFormTemplate = forwardRef(
  TravellerPreferencesFormTemplateWithRef
);

export { TravellerPreferencesFormTemplate };
export default TravellerPreferencesForm;
